import { computed, ref } from "vue";
import axios from "axios";
import { defineStore, storeToRefs } from "pinia";
import { isEqual } from "lodash";
import CustomStore from "devextreme/data/custom_store";
import { createActionCableSubscription } from "@/js-modules/actionCableChannel.js";
import { useCabinetStore } from "@/stores/cabinetStore.js";
import { useUserStore } from "@/stores/userStore.js";
import { frontendTabData } from "@/js-modules/frontendTabData.js";
import { useToastStore } from "@/stores/toastStore.js";

export const useSearchPageStore = defineStore('search-page-store',  () => {
  const cabinetStore = useCabinetStore()
  const { cabinetId } = storeToRefs(cabinetStore)
  const userStore = useUserStore()
  const { userId } = storeToRefs(userStore)
  const toastStore = useToastStore()

  const searchGridInstance = ref(null)
  const searchResultCount = ref(0)
  const basketCount = ref(0)

  const selectedOnly = ref(false)
  const headerFilter = ref()
  const searchBarObjects = ref([])

  const docQueryParams = computed(() => {
    return {
      selected_only: selectedOnly.value,
      header_filter: headerFilter.value,
      search_bar_objects: searchBarObjects.value,
    }
  })

  const activeDocQueryParams = ref(null)

  const setDocQueryParams = () => {
    activeDocQueryParams.value = docQueryParams.value
  }

  const resetSearchAndFilter = () => {
    selectedOnly.value = false
    headerFilter.value = null
    searchBarObjects.value = []
  }

  const refreshData = () => {
    searchGridInstance.value.refresh()
  }

  const scrollToTop = () => {
    searchGridInstance.value.pageIndex(0)
  }

  const tagFilterItems = ref([])
  createActionCableSubscription(
    'TagFilterItemsChannel',
    { tab_id: frontendTabData.tabId },
    (payload) => { tagFilterItems.value = payload }
  )

  const searchHistoryItems = ref([])
  createActionCableSubscription(
    'SearchHistoryItemsChannel',
    { tab_id: frontendTabData.tabId },
    (payload) => { searchHistoryItems.value = payload }
  )

  const selectAllState = ref(false)
  createActionCableSubscription(
    'SelectAllStateChannel',
    { tab_id: frontendTabData.tabId },
    (payload) => { selectAllState.value = payload.state }
  )

  const tagEditorItems = ref([]);
  createActionCableSubscription(
    'TagEditorItemsChannel',
    { mode_params: { mode: "user_selections", user_id: userId.value, cabinet_id: cabinetId.value } },
    (payload) => { tagEditorItems.value = payload }
  )

  createActionCableSubscription(
    'BasketCountChannel',
    { user_id: userId.value, cabinet_id: cabinetId.value },
    (payload) => { basketCount.value = payload }
  )

  const selectDocument = (docId, selected, cabinetId) => {
    const params = {
      doc_id: docId,
      selected: selected,
      cabinet_id: cabinetId
    }

    axios.post('/api/select_document', params).then(_response => {
      // update the cached data so that when you scroll down and back up you don't lose the change
      searchGridDataStore.push([{
        type: 'update',
        key: docId,
        data: { selected: selected }
      }])
    })
  }

  const selectAll = (selected, cabinetId) => {
    const params = {
      selected: selected,
      cabinet_id: cabinetId
    }

    axios.post('/api/select_all', params).then(_response => {
      refreshData() // update the data so that cached checkbox states are cleared
    })
  }

  const editTags = (modeParams, tagsToAdd, tagsToRemove) => {
    const params = {
      mode_params: modeParams,
      tags_to_add: tagsToAdd,
      tags_to_remove: tagsToRemove
    }

    axios.post('/api/edit_tags', params).then(r => {
      refreshData()
      toastStore.showToastMessage('Tags updated', 'success', 'add')
    })
  }

  const editMetadata = (modeParams, fields) => {
    const params = {
      mode_params: modeParams,
      fields: fields
    }

    axios.post('/api/edit_metadata', params).then(r => {
      refreshData()
      toastStore.showToastMessage('Metadata updated', 'success', 'add')
    })
  }

  const fetchHeadlines = async (docId, searchStrings) => {
    const params = {
      doc_id: docId,
      search_strings: searchStrings
    }

    const response = await axios.get('/api/headline', { params })
    return response.data.headlines
  }

  const fetchFoldersByPath = async (currentPath) => {
    const params = {
      current_path: currentPath,
      cabinet_id: cabinetId.value
    }

    const response = await axios.get('/api/folders_by_path', { params })
    return response.data
  }

  const emptyBasket = () => {
    const params = {
      cabinet_id: cabinetId.value
    }

    axios.post('/api/empty_basket', params).then(_response =>{
      refreshData()
    })
  }

  const generateBasket = async () => {
    const params = {
      cabinet_id: cabinetId.value
    }

    const response = await axios.post('/api/generate_basket', params)
    return response.data.basket_id
  }

  // params used in the last load to detect if need to start a new search or not
  const lastParams = ref(null)
  const searchGridDataStore = new CustomStore({
    key: "doc_id",
    load: async (loadOptions) => {
      const params = {
        cabinet_id: cabinetId.value,
        skip: loadOptions.skip,
        take: loadOptions.take,
        sort: JSON.stringify(loadOptions.sort),
        doc_query_params: JSON.stringify(docQueryParams.value)
      }

      // The custom store's load function can be triggered by many sources
      // changing the sort, selecting from a header filter dropdown, typing  into the filter row, changing the docQueryParams
      const { skip, take, ...comparableParams } = params
      const newSearch = !isEqual(comparableParams, lastParams.value)
      lastParams.value = comparableParams

      let response
      if (newSearch)
        response = await axios.get("/api/search_grid/start_search", { params })
      else
        response = await axios.get("/api/search_grid/get_page", { params })

      const documents = response.data.documents
      const totalCount = response.data.totalCount

      searchResultCount.value = totalCount
      return {
        data: documents,
        totalCount: totalCount
      }
    },
    update: async (docId, newValue) => {
      const params = {
        mode_params: { mode: "document", doc_id: docId },
        fields: newValue
      }

      await axios.post('/api/edit_metadata', params)
      toastStore.showToastMessage('Metadata updated', 'success', 'add')
      return newValue
    }
  })

  const createHeaderFilterStore = (dataField) => {
    return {
      store: new CustomStore({
        load: async (loadOptions) => {
          const params = {
            data_field: dataField,
            search_text: loadOptions.searchValue || '',
          }

          const response = await axios.get("/api/search_grid/header_dropdown", { params })

          return {
            data: response.data.dropdownOptions,
            totalCount: response.data.totalCount
          }
        }
      }),
      paginate: false
    }
  }

  return {
    basketCount,
    searchGridInstance,
    searchResultCount,
    selectedOnly,
    headerFilter,
    searchBarObjects,
    docQueryParams,
    activeDocQueryParams,
    setDocQueryParams,
    resetSearchAndFilter,
    searchGridDataStore,
    createHeaderFilterStore,
    tagFilterItems,
    searchHistoryItems,
    tagEditorItems,
    selectAllState,
    selectDocument,
    selectAll,
    editTags,
    editMetadata,
    fetchHeadlines,
    fetchFoldersByPath,
    emptyBasket,
    generateBasket,
    refreshData,
    scrollToTop,
    lastParams
  }
})
